// src/components/AppLayout.tsx
import { ReactNode, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "./AppSidebar";
import { AppHeader } from "./AppHeader";
import { supabase } from "../supabaseClient";

interface AppLayoutProps {
  children: ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const location = useLocation();
  const [isPaidUser, setIsPaidUser] = useState<boolean | null>(null);
  const PROFILE_UPDATED_EVENT = "innergem:profile-updated";

  useEffect(() => {
    const checkSubscription = async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();

        // 未登录时不在这里做遮罩控制（比如 404）
        if (!user) {
          setIsPaidUser(true);
          return;
        }

        const { data: profile } = await supabase
          .from("user_profiles")
          .select("has_lifetime_deal, subscription_tier")
          .eq("id", user.id)
          .maybeSingle();

        const paidTiers = [
          "beginner",
          "intermediate",
          "upper_intermediate",
          "lifetime_deal",
          "beginner_monthly",
          "beginner_yearly",
          "intermediate_monthly",
          "intermediate_yearly",
          "upper_intermediate_monthly",
          "upper_intermediate_yearly",
        ];

        const rawTier = (profile as any)?.subscription_tier as string | null;
        const tier = rawTier ? rawTier.toLowerCase().replace(/\s+/g, "_") : null;
        const hasLifetimeDeal = !!profile?.has_lifetime_deal;
        const hasPaidTier = tier ? paidTiers.includes(tier) : false;

        setIsPaidUser(hasLifetimeDeal || hasPaidTier);
      } catch (err) {
        console.warn("Failed to check subscription tier:", err);
        setIsPaidUser(false);
      }
    };

    const onProfileUpdated = () => {
      void checkSubscription();
    };

    checkSubscription();
    window.addEventListener(PROFILE_UPDATED_EVENT, onProfileUpdated);
    return () => {
      window.removeEventListener(PROFILE_UPDATED_EVENT, onProfileUpdated);
    };
  }, []);

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-[#FBF5FF]">
        {/* 左边：Sidebar，会根据 collapsible 自动变宽 / 变窄 */}
        <AppSidebar />

        {/* 右边：整块内容区（包含 topbar + 页面） */}
        <div className="flex min-h-screen w-full flex-1 flex-col">
          {/* 顶部：sticky header（刚刚那一份） */}
          <AppHeader />

          {/* 下面是可滚动的主内容区 */}
          <main className="flex-1 overflow-y-auto">
            {/* ✅ 这里统一控制“页面居中”和最大宽度 */}
            <div className="relative mx-auto w-full max-w-5xl px-4 pb-10 pt-8">
              {children}

              {/* 未付费用户时加一层蒙砂玻璃，遮住主内容但不影响 Sidebar / Header。
                  设置页需要可输入兑换码，因此不加遮罩。 */}
              {isPaidUser === false && location.pathname !== "/settings" && (
                <div className="pointer-events-auto absolute inset-0 z-20 flex items-center justify-center bg-white/30 backdrop-blur-sm">
                  <div className="max-w-sm rounded-2xl bg-white/80 px-6 py-4 text-center shadow-lg">
                    <p className="text-sm font-semibold text-primary">
                      订阅解锁
                    </p>
                    <p className="mt-2 text-xs text-muted-foreground">
                      订阅任一 Inner Gem 方案，即可解锁此页面的全部内容。
                    </p>
                  </div>
                </div>
              )}
            </div>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
